import {
  Box,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import get from 'lodash-es/get';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { API_USERS_TRAFFICKER_LIST_URL } from '../../constants/apiUrls';
import { useConfig } from '../../hooks/useConfig';
import { useConstants } from '../../hooks/useConstants';
import { isAchHost } from '../../util/achAuth';
import { unwrapAchBareCollection } from '../../util/achEnvelope';
import { getRequestJSON } from '../../util/services';
import CheckboxArray from '../forms/CheckboxArray';
import Select from '../forms/Select';
import TextField from '../forms/TextField';

function ClientsFormDefaults() {
  // The traffickers role list opts into the per-domain flip (ACH-colk); ACH
  // wraps the Rails bare array in the SPEC §2 {collection} envelope — unwrap.
  const { apiHostFor } = useConfig();
  const usersHost = apiHostFor('users');
  const { constants } = useConstants();
  const [traffickersList, setTraffickersList] = useState([]);
  const { watch } = useFormContext();

  const ACCEPTED_VCR_WHITELIST_TIERS = get(constants, 'CAMPAIGN.ACCEPTED_VCR_WHITELIST_TIERS', []).map(
    ([value, label]) => ({ value, label })
  );
  const ACCEPTED_WHITELIST_TIERS = get(constants, 'CAMPAIGN.ACCEPTED_WHITELIST_TIERS', []).map(([value, label]) => ({
    value,
    label,
  }));
  const ACCEPTED_IO_OPTIMIZATION_TYPES = get(constants, 'INSERTION_ORDER.ACCEPTED_IO_OPTIMIZATION_TYPES', []).map(
    (value) => ({ value, label: value })
  );

  useEffect(() => {
    const getTraffickers = async () => {
      const response = await getRequestJSON(usersHost, API_USERS_TRAFFICKER_LIST_URL);
      setTraffickersList(unwrapAchBareCollection(response, isAchHost(usersHost)));
    };

    getTraffickers();
  }, [usersHost]);

  const optimizationType = watch('clientDrivenDefaults.insertionOrder.optimizationType.defaultValue');

  const isShowMinCtrOptimization =
    optimizationType === ACCEPTED_IO_OPTIMIZATION_TYPES[0]?.value ||
    optimizationType === ACCEPTED_IO_OPTIMIZATION_TYPES[2]?.value;

  return (
    <>
      <Box mt={4}>
        <Typography>Advertiser Defaults</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50%">Field</TableCell>
                <TableCell width="50%">Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <InputLabel id="Trafficker">Trafficker</InputLabel>
                </TableCell>
                <TableCell>
                  <Select
                    label=""
                    labelId="Trafficker"
                    name="clientDrivenDefaults.advertiser.traffickerId.defaultValue"
                    fullWidth
                    options={traffickersList.map(({ id, name }) => ({ value: id, label: name }))}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box mt={4}>
        <Typography>Contract Defaults</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50%">Field</TableCell>
                <TableCell width="50%">Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Contract CPM</TableCell>
                <TableCell>
                  <TextField
                    label=""
                    inputProps={{
                      'aria-label': 'Contract CPM',
                    }}
                    name="clientDrivenDefaults.contract.contractCpm.defaultValue"
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Pacing Strategy</TableCell>
                <TableCell>
                  <CheckboxArray
                    row
                    name="clientDrivenDefaults.contract.pacingStrategies.defaultValue"
                    options={[
                      {
                        value: 'slowstart',
                        label: 'Slow Start',
                      },
                      {
                        value: 'fastcatchup',
                        label: 'Fast Catch Up',
                      },
                    ]}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box mt={4}>
        <Typography>Insertion Order Defaults</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50%">Field</TableCell>
                <TableCell width="50%">Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <InputLabel id="OptimizationType">Optimization Type</InputLabel>
                </TableCell>
                <TableCell>
                  <Select
                    label=""
                    labelId="OptimizationType"
                    name="clientDrivenDefaults.insertionOrder.optimizationType.defaultValue"
                    options={ACCEPTED_IO_OPTIMIZATION_TYPES}
                  />
                </TableCell>
              </TableRow>
              {isShowMinCtrOptimization && (
                <TableRow>
                  <TableCell>Min. CTR Optimization</TableCell>
                  <TableCell>
                    <TextField
                      label=""
                      inputProps={{ 'aria-label': 'Min. CTR Optimization' }}
                      name="clientDrivenDefaults.insertionOrder.minCtrOptimization.defaultValue"
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box mt={4}>
        <Typography>Campaign Defaults</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50%">Field</TableCell>
                <TableCell width="50%">Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Max Bid%</TableCell>
                <TableCell>
                  <TextField
                    label=""
                    inputProps={{ 'aria-label': 'Max Bid%' }}
                    name="clientDrivenDefaults.campaign.maxBid.defaultValue"
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box mt={4}>
        <Typography>Targeting Profile</Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50%">Field</TableCell>
                <TableCell width="50%">Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <InputLabel id="UserAutoAppInclusionCTRTier">User Auto App Inclusion CTR Tier</InputLabel>
                </TableCell>
                <TableCell>
                  <Select
                    label=""
                    labelId="UserAutoAppInclusionCTRTier"
                    name="clientDrivenDefaults.targetingProfile.appWhitelistTier.defaultValue"
                    options={ACCEPTED_WHITELIST_TIERS}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>User Auto App Exclusion Threshold (%)</TableCell>
                <TableCell>
                  <TextField
                    label=""
                    inputProps={{ 'aria-label': 'User Auto App Exclusion Threshold (%)' }}
                    name="clientDrivenDefaults.targetingProfile.appBlacklistThreshold.defaultValue"
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <InputLabel id="UserAutoAppInclusionVCRTier">User Auto App Inclusion VCR Tier</InputLabel>
                </TableCell>
                <TableCell>
                  <Select
                    label=""
                    labelId="UserAutoAppInclusionVCRTier"
                    name="clientDrivenDefaults.targetingProfile.appVcrWhitelistTier.defaultValue"
                    options={ACCEPTED_VCR_WHITELIST_TIERS}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>User Auto App Exclusion VCR Threshold (%)</TableCell>
                <TableCell>
                  <TextField
                    label=""
                    inputProps={{ 'aria-label': 'User Auto App Exclusion VCR Threshold (%)' }}
                    name="clientDrivenDefaults.targetingProfile.appVcrBlacklistThreshold.defaultValue"
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
}

export default ClientsFormDefaults;
